/**
 * Shared entity URL resolution — used by CommandPalette and SearchPage.
 *
 * Centralised here to avoid duplication and make the URL mapping testable.
 */

export interface EntitySearchResult {
  entity_type: string;
  entity_id: string;
  name: string;
  metadata?: Record<string, string>;
}

/**
 * Returns the client-side route URL for a given entity search result.
 *
 * Special cases:
 * - `meeting_instance` needs `metadata.series_id` for the nested route.
 *   Falls back to `/meetings` when missing.
 * - `tag` and `quick_capture` navigate to their list pages (no detail route).
 * - `excerpt` (#277) opens the item it was excerpted from, named by
 *   `metadata.source_type` / `source_id` (+ `source_series_id` for a series
 *   meeting). Without them it falls back to `/`.
 */
export function getEntityUrl(result: EntitySearchResult): string {
  switch (result.entity_type) {
    case 'task': return `/tasks/${result.entity_id}`;
    case 'meeting_instance': {
      const seriesId = result.metadata?.series_id;
      return seriesId
        ? `/meetings/${seriesId}/${result.entity_id}`
        : `/meetings/standalone/${result.entity_id}`;
    }
    case 'meeting_series': return `/meetings/${result.entity_id}`;
    case 'person': return `/people/${result.entity_id}`;
    case 'tag': return '/tags';
    case 'scope': return `/projects/${result.entity_id}`;
    case 'reference_doc': return `/views/${result.entity_id}`;
    case 'quick_capture': return '/inbox';
    case 'note': return `/notes/${result.entity_id}`;
    case 'attachment': return `/api/attachments/${result.entity_id}/download`;
    case 'scratchpad': return `/scratchpad/${result.entity_id}`;
    case 'excerpt': {
      const type = result.metadata?.source_type;
      const id = result.metadata?.source_id;
      // Only document types can be a source; anything else (or a loop back to
      // 'excerpt') is malformed metadata.
      if (!id || !type || !['task', 'note', 'meeting_instance', 'scratchpad'].includes(type)) return '/';
      const seriesId = result.metadata?.source_series_id;
      return getEntityUrl({
        entity_type: type,
        entity_id: id,
        name: result.name,
        metadata: seriesId ? { series_id: seriesId } : undefined,
      });
    }
    default: return '/';
  }
}

const RECENT_SEARCHES_KEY = 'dossier:recentSearches';
const MAX_RECENT = 5;

export function getRecentSearches(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveRecentSearch(query: string) {
  const trimmed = query.trim();
  if (!trimmed) return;
  const recent = getRecentSearches().filter(s => s !== trimmed);
  recent.unshift(trimmed);
  localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
}
